import { Link } from "react-router-dom";
import { ClipboardList, Sparkles, Users, TrendingDown, ArrowRight } from "lucide-react";

/**
 * Unified CTA for the Tracker + Community product.
 * Community membership and Tracker Pro are the same product ($19/month).
 *
 * Variants:
 *   - hero     : full-width homepage section
 *   - card     : side-by-side card (fits inside grids)
 *   - inline   : horizontal banner for inside articles or tool results
 *   - compact  : minimal chip/button
 */
export default function TrackerCTA({ variant = "card", source = "generic" }) {
  const href = "/tracker/login";
  const testid = `tracker-cta-${variant}-${source}`;

  if (variant === "compact") {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2D6A4F] text-white text-sm font-semibold hover:bg-[#1B4332] transition-colors"
        data-testid={testid}
      >
        <ClipboardList className="w-3.5 h-3.5" /> Open Tracker
      </Link>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className="my-8 rounded-2xl border border-[#2D6A4F] bg-gradient-to-br from-[#E9EDC9] to-[#F2F0E9] p-5 md:p-6"
        data-testid={testid}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#2D6A4F] mb-1.5">
              MetabolicLabOS Tracker · $19 / month
            </p>
            <p className="text-base md:text-lg font-semibold text-[#1A2018] leading-snug">
              Log daily, break plateaus, and ask Kai unlimited questions.
            </p>
          </div>
          <Link
            to={href}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1A2018] text-white font-semibold hover:bg-[#2D6A4F] transition-colors whitespace-nowrap"
            data-testid={`${testid}-btn`}
          >
            Start tracking <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (variant === "hero") {
    return (
      <section
        className="section-padding bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] text-white relative overflow-hidden dark-section"
        data-testid={testid}
      >
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="inline-block text-xs font-semibold uppercase tracking-wider bg-[#E9EDC9] text-[#1B4332] px-3 py-1 rounded-full mb-4">
                New · The Tracker
              </p>
              <h2 className="mb-4 text-white">
                Your GLP-1 journey, tracked daily. In one app.
              </h2>
              <p className="text-lg opacity-90 mb-6 leading-relaxed">
                60-second check-ins, weight trends, plateau alerts, and unlimited
                educational Kai chat — all in a mobile-first app you can install on your phone.
              </p>
              <ul className="space-y-2 mb-8">
                {[
                  "Daily action engine tailored to your logs",
                  "Kai Unlimited (no daily message cap)",
                  "Weekly Coach Report emailed to you",
                  "Add to Home Screen on iPhone and Android",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm opacity-95">
                    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#E9EDC9]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/meet-tracker"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-[#1B4332] font-semibold hover:bg-[#E9EDC9] transition-colors"
                  data-testid={`${testid}-primary`}
                >
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={href}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/15 border border-white/30 text-white font-semibold hover:bg-white/25 transition-colors"
                  data-testid={`${testid}-secondary`}
                >
                  Start free
                </Link>
              </div>
              <p className="text-xs opacity-70 mt-4">
                Educational only. Not medical advice.
              </p>
            </div>
            <div className="hidden md:grid grid-cols-2 gap-3">
              <HeroStat icon={ClipboardList} label="Check-in" value="60 sec / day" />
              <HeroStat icon={TrendingDown} label="Weight trend" value="7 / 14 / 30 d" />
              <HeroStat icon={Sparkles} label="Kai chat" value="Unlimited" />
              <HeroStat icon={Users} label="Install" value="iOS + Android" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: card
  return (
    <div
      className="rounded-2xl border border-[#E8E4DC] bg-white p-5 md:p-6"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#E9EDC9] flex items-center justify-center">
          <ClipboardList className="w-4 h-4 text-[#2D6A4F]" />
        </div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">
          MetabolicLabOS Tracker
        </p>
      </div>
      <h3 className="text-lg font-semibold text-[#1A2018] mb-2">
        Keep the momentum going
      </h3>
      <p className="text-sm text-[#4B5548] leading-relaxed mb-4">
        Log daily check-ins, get instant symptom tips, and ask Kai unlimited
        questions in the mobile Tracker.
      </p>
      <Link
        to={href}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2D6A4F] text-white text-sm font-semibold hover:bg-[#1B4332] transition-colors"
        data-testid={`${testid}-btn`}
      >
        Start tracking <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white/15 border border-white/25 p-4 backdrop-blur-sm">
      <Icon className="w-5 h-5 text-[#E9EDC9] mb-2" />
      <p className="text-[11px] uppercase tracking-wider text-[#E9EDC9]">{label}</p>
      <p className="text-base font-semibold text-white mt-1">{value}</p>
    </div>
  );
}
